import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  Check,
  CircleDashed,
  Clock,
  Filter,
  Loader2,
  Search,
  ShieldX,
  Sparkles,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { EmptyState as SharedEmptyState } from '@/components/ui/empty-state'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BigNumber,
  EcosystemMark,
  Eyebrow,
} from '@/components/hawk-primitives'

type Status = 'pending' | 'verified' | 'unverified' | 'approved' | 'denied'

type AdmissionStatus = 'pending' | 'running' | 'succeeded' | 'failed' | null

interface RequestRow {
  id: string
  repository_name: string
  package_name: string
  package_version: string | null
  ecosystem: string
  requested_by: string
  requested_at: string
  note: string | null
  status: Status
  check_status: number | null
  check_result: Record<string, unknown> | null
  checked_at: string | null
  decided_by: string | null
  decided_at: string | null
  decision_reason: string | null
  admission_status: AdmissionStatus
  admission_started_at: string | null
  admission_finished_at: string | null
  admission_result: Record<string, unknown> | null
  requester_email: string | null
  requester_name: string | null
  decider_email: string | null
}

export const Route = createFileRoute('/_app/org/requests')({
  component: AdminRequestsPage,
})

function AdminRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('open')
  const [search, setSearch] = useState('')

  // "open" pseudo-status = pending + verified + unverified, handled in-memory.
  const requestPath = useMemo(() => {
    const params = new URLSearchParams()
    if (statusFilter !== 'open' && statusFilter !== 'all') {
      params.set('status', statusFilter)
    }
    const qs = params.toString()
    return `/admin/requests${qs ? '?' + qs : ''}`
  }, [statusFilter])

  const requests = useOrgQuery<RequestRow[]>(
    ['admin-requests', statusFilter],
    requestPath,
    {
      // Tighten refresh while any admission is in-flight so the UI feels live.
      refetchInterval: (q) => {
        const data = Array.isArray(q.state.data) ? q.state.data : []
        const hasActiveAdmission = data.some(
          (r) => r.admission_status === 'pending' || r.admission_status === 'running',
        )
        return hasActiveAdmission ? 2_000 : 20_000
      },
    },
  )

  const all = Array.isArray(requests.data) ? requests.data : []
  const visible = useMemo(() => {
    let list = all
    if (statusFilter === 'open') {
      list = list.filter((r) =>
        ['pending', 'verified', 'unverified'].includes(r.status),
      )
    }
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(
        (r) =>
          r.package_name.toLowerCase().includes(q) ||
          r.repository_name.toLowerCase().includes(q) ||
          r.requester_email?.toLowerCase().includes(q),
      )
    }
    return list
  }, [all, statusFilter, search])

  const counts = useMemo(() => {
    const c = { open: 0, approved: 0, denied: 0, total: all.length }
    for (const r of all) {
      if (['pending', 'verified', 'unverified'].includes(r.status)) c.open++
      else if (r.status === 'approved') c.approved++
      else if (r.status === 'denied') c.denied++
    }
    return c
  }, [all])

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Package requests"
        description="Review and decide on incoming requests to admit packages into repositories. The system performs an upstream check at submission time."
      />

      {/* KPI band */}
      <div className="grid grid-cols-2 lg:grid-cols-3 border border-border rounded-md bg-card/40 overflow-hidden mb-8">
        <Tile
          label="Open"
          value={counts.open}
          icon={<Clock className="size-3.5" strokeWidth={1.5} />}
          accent
        />
        <Tile
          label="Approved"
          value={counts.approved}
          icon={<Check className="size-3.5" strokeWidth={1.5} />}
        />
        <Tile
          label="Denied"
          value={counts.denied}
          icon={<ShieldX className="size-3.5" strokeWidth={1.5} />}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by package, repo, requester..."
            className="pl-9 bg-secondary border-transparent focus:border-primary/30 font-mono text-xs"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="w-48 bg-secondary border-transparent"
            aria-label="Filter by status"
          >
            <Filter className="size-3.5 mr-2 text-muted-foreground" aria-hidden />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open (pending)</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
          {visible.length} shown
        </div>
      </div>

      {requests.isLoading ? (
        <div className="border border-border bg-card/30 rounded-md h-[320px] hawk-shimmer" />
      ) : visible.length === 0 ? (
        <EmptyState statusFilter={statusFilter} hasSearch={!!search} />
      ) : (
        <ul className="space-y-3">
          {visible.map((r) => (
            <RequestCard key={r.id} request={r} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Tile({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon: React.ReactNode
  accent?: boolean
}) {
  return (
    <div className="px-6 py-5 border-r border-border last:border-r-0">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </span>
        <span
          className={
            accent ? 'text-primary' : 'text-muted-foreground/50'
          }
        >
          {icon}
        </span>
      </div>
      <BigNumber value={value.toString()} className="text-[36px]" />
    </div>
  )
}

function RequestCard({ request }: { request: RequestRow }) {
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState<'approve' | 'deny' | null>(null)
  const [reason, setReason] = useState('')

  const isOpen = ['pending', 'verified', 'unverified'].includes(request.status)
  const verified = request.status === 'verified' || request.check_status === 200
  const checkUrl = (request.check_result?.url as string) ?? null

  const mutation = useMutation({
    mutationFn: async () => {
      const path =
        decision === 'approve'
          ? orgPath(`/admin/requests/${request.id}/approve`)
          : orgPath(`/admin/requests/${request.id}/deny`)
      const r = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() || null }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      toast.success(
        decision === 'approve' ? 'Request approved' : 'Request denied',
      )
      queryClient.invalidateQueries({ queryKey: ['admin-requests'] })
      setDecision(null)
      setReason('')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <li className="relative border border-border bg-card rounded-md overflow-hidden animate-hawk-rise">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-0">
        {/* Left: meta */}
        <div className="p-5 lg:border-r border-border">
          <div className="flex items-start gap-4">
            <EcosystemMark ecosystem={request.ecosystem} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Link
                  to="/packages/$repo"
                  params={{ repo: request.repository_name }}
                  search={{ q: request.package_name }}
                  className="text-lg font-medium tracking-tight text-foreground truncate hover:text-primary transition-colors"
                  title={`Browse ${request.package_name} in ${request.repository_name}`}
                >
                  {request.package_name}
                </Link>
                {request.package_version && (
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-primary/10 text-primary ring-1 ring-inset ring-primary/30 tabular-nums">
                    {request.package_version}
                  </span>
                )}
                <StatusBadge status={request.status} />
              </div>
              <div className="font-mono text-[11px] text-muted-foreground mb-3">
                in{' '}
                <Link
                  to="/packages/$repo"
                  params={{ repo: request.repository_name }}
                  className="text-foreground/80 hover:text-primary transition-colors underline-offset-4 hover:underline"
                >
                  {request.repository_name}
                </Link>
              </div>

              <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-xs">
                <Dt>Requested by</Dt>
                <Dd>
                  <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                    <span className="text-foreground/90">
                      {request.requester_name ??
                        request.requester_email ??
                        '—'}
                    </span>
                    <span className="text-muted-foreground">
                      {formatDate(request.requested_at)}
                    </span>
                  </div>
                </Dd>

                <Dt>Upstream check</Dt>
                <Dd>
                  <span
                    className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider ${
                      verified
                        ? 'text-primary'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {verified ? (
                      <Sparkles className="size-3" strokeWidth={1.5} />
                    ) : (
                      <CircleDashed className="size-3" strokeWidth={1.5} />
                    )}
                    {request.status === 'verified'
                      ? 'Verified upstream'
                      : 'Unverified'}
                    {request.check_status !== null && (
                      <span className="opacity-60 ml-2 pl-2 border-l border-current/30">
                        HTTP {request.check_status}
                      </span>
                    )}
                  </span>
                  {checkUrl && (
                    <div className="font-mono text-[10px] text-muted-foreground/70 truncate mt-0.5">
                      {checkUrl}
                    </div>
                  )}
                </Dd>

                {request.note && (
                  <>
                    <Dt>Reason</Dt>
                    <Dd>
                      <span className="text-foreground/90 italic">
                        “{request.note}”
                      </span>
                    </Dd>
                  </>
                )}

                {!isOpen && request.decided_at && (
                  <>
                    <Dt>
                      {request.status === 'approved' ? 'Approved by' : 'Denied by'}
                    </Dt>
                    <Dd>
                      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                        <span className="text-foreground/90">
                          {request.decider_email ?? '—'}
                        </span>
                        <span className="text-muted-foreground">
                          {formatDate(request.decided_at)}
                        </span>
                      </div>
                      {request.decision_reason && (
                        <div className="text-muted-foreground italic mt-0.5">
                          “{request.decision_reason}”
                        </div>
                      )}
                    </Dd>
                  </>
                )}

                {request.status === 'approved' && request.admission_status && (
                  <>
                    <Dt>Admission</Dt>
                    <Dd>
                      <AdmissionStateLine request={request} />
                    </Dd>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="p-5 bg-background/30 flex flex-col gap-2 justify-center">
          {isOpen ? (
            <>
              <Button
                onClick={() => setDecision('approve')}
                className="w-full gap-2"
              >
                <Check className="size-4" strokeWidth={1.5} />
                Approve
              </Button>
              <Button
                onClick={() => setDecision('deny')}
                variant="ghost"
                className="w-full gap-2 text-muted-foreground hover:text-destructive"
              >
                <X className="size-4" strokeWidth={1.5} />
                Deny
              </Button>
            </>
          ) : (
            <div className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {request.status === 'approved' ? 'Approved' : 'Denied'}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={decision !== null}
        onOpenChange={(o) => !o && setDecision(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decision === 'approve' ? 'Approve request' : 'Deny request'}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const pkg = request.package_version
                  ? `${request.package_name}@${request.package_version}`
                  : request.package_name
                return decision === 'approve'
                  ? `Admit ${pkg} into ${request.repository_name}.`
                  : `Deny the request to admit ${pkg} into ${request.repository_name}.`
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Reason {decision === 'approve' ? '(optional)' : '(recommended)'}
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                decision === 'approve'
                  ? 'Note about why this is being approved'
                  : 'Why is this being denied?'
              }
              maxLength={1000}
              className="mt-2"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)}>
              Cancel
            </Button>
            <Button
              variant={decision === 'deny' ? 'destructive' : 'default'}
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? 'Saving...'
                : decision === 'approve'
                  ? 'Approve'
                  : 'Deny'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  )
}

function AdmissionStateLine({ request }: { request: RequestRow }) {
  const status = request.admission_status
  const result = request.admission_result ?? {}
  const filesCount =
    typeof (result as Record<string, unknown>).files_count === 'number'
      ? ((result as Record<string, number>).files_count as number)
      : null
  const errMsg =
    typeof (result as Record<string, unknown>).message === 'string'
      ? ((result as Record<string, string>).message as string)
      : null

  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        <Clock className="size-3" strokeWidth={1.5} />
        Queued
      </span>
    )
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-primary">
        <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
        Downloading files
      </span>
    )
  }
  if (status === 'succeeded') {
    return (
      <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-primary">
        <span className="inline-flex items-center gap-1.5">
          <Check className="size-3" strokeWidth={1.5} />
          Stored
        </span>
        {filesCount !== null && (
          <span className="opacity-60">
            {filesCount} {filesCount === 1 ? 'file' : 'files'}
          </span>
        )}
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-destructive">
          <ShieldX className="size-3" strokeWidth={1.5} />
          Failed
        </span>
        {errMsg && (
          <div className="font-mono text-[10px] text-destructive/80 mt-0.5 break-all">
            {errMsg}
          </div>
        )}
      </div>
    )
  }
  return null
}

function StatusBadge({ status }: { status: Status }) {
  const map: Record<Status, { label: string; cls: string }> = {
    pending: {
      label: 'Pending',
      cls: 'bg-secondary text-muted-foreground ring-border',
    },
    verified: {
      label: 'Verified',
      cls: 'bg-primary/10 text-primary ring-primary/30',
    },
    unverified: {
      label: 'Unverified',
      cls: 'bg-secondary text-muted-foreground ring-border',
    },
    approved: {
      label: 'Approved',
      cls: 'bg-primary/10 text-primary ring-primary/30',
    },
    denied: {
      label: 'Denied',
      cls: 'bg-destructive/10 text-destructive ring-destructive/30',
    },
  }
  const { label, cls } = map[status]
  return (
    <span
      className={`inline-flex items-center font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  )
}

function Dt({ children }: { children: React.ReactNode }) {
  return (
    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-0.5">
      {children}
    </dt>
  )
}

function Dd({ children }: { children: React.ReactNode }) {
  return <dd className="min-w-0">{children}</dd>
}

function EmptyState({
  statusFilter,
  hasSearch,
}: {
  statusFilter: string
  hasSearch: boolean
}) {
  return (
    <SharedEmptyState
      eyebrow="Inbox empty"
      title={
        hasSearch
          ? 'No matches.'
          : statusFilter === 'open'
            ? 'No open requests.'
            : 'Nothing to show here.'
      }
      description={
        hasSearch
          ? 'Try a different search term.'
          : 'When users submit package requests, they will appear here for review.'
      }
    />
  )
}
